/*
 * kica-digital-cart.js — KICA-specific cart UI hooks for digital products.
 *
 * Extracted from inline <script> blocks in layout/theme.liquid so the code
 * loads deferred (after parse), is browser-cached, and stops attributing
 * runtime cost back to the page HTML in Lighthouse.
 *
 * Two responsibilities:
 *
 *  1. Sticky-bar freeze on ATC for digital products.
 *     When the user clicks an Add-to-Cart button tagged `data-digital="true"`,
 *     swap its label to "Adding..." → "In Cart" and hide the floating
 *     product-sticky-form bar. The freeze guard re-applies the frozen state
 *     for up to 4s in case the theme re-renders the button afterwards.
 *
 *  2. Robust cart cleaner.
 *     KICA sells digital classes/e-books where quantity must always be 1.
 *     On every page load we sweep /cart.js and silently set qty=1 for any
 *     digital line item whose quantity drifted higher. We then dispatch
 *     cart:refresh/build/update events so the header drawer re-renders
 *     without a full page reload.
 */

(function () {
  'use strict';

  // ============================================================
  // 1. Sticky-bar freeze on digital ATC
  // ============================================================

  const STICKY_SELECTOR = '.product-sticky-form, [id^="ProductStickyForm"], product-sticky-form';

  function hideStickyBar() {
    const bars = document.querySelectorAll(STICKY_SELECTOR);
    bars.forEach(bar => {
      bar.style.setProperty('display', 'none', 'important');
      bar.style.opacity = '0';
      bar.style.visibility = 'hidden';
    });
  }

  function freezeButton(btn) {
    if (!btn) return;
    btn.style.opacity = '0.7';
    btn.style.pointerEvents = 'none';
    btn.style.cursor = 'default';

    const textSpan = btn.querySelector('.btn-text span') || btn.querySelector('.btn-text') || btn.querySelector('span');
    if (textSpan) textSpan.textContent = 'In Cart';

    const priceSpan = btn.querySelector('.price, product-buy-price');
    if (priceSpan) priceSpan.style.display = 'none';
  }

  function checkOnLoad() {
    const inCartFlag = document.querySelector('.product-sticky-form__card[data-in-cart="true"]');
    if (inCartFlag) hideStickyBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkOnLoad);
  } else {
    checkOnLoad();
  }
  // Safety net for late-rendered sticky bars.
  setTimeout(checkOnLoad, 500);

  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-digital="true"]');
    if (!btn) return;

    // Form-id grouping: find all sibling buttons that submit the same form.
    let buttonsToUpdate = [btn];
    let formId = btn.getAttribute('form');
    if (!formId) {
      const parentForm = btn.closest('form');
      if (parentForm) formId = parentForm.id;
    }
    if (formId) {
      const siblings = document.querySelectorAll(
        `button[form="${formId}"][data-digital="true"], form[id="${formId}"] button[data-digital="true"]`
      );
      if (siblings.length > 0) {
        buttonsToUpdate = Array.from(new Set(siblings));
      }
    }

    // Stage 1: Adding... visual.
    buttonsToUpdate.forEach(b => {
      const s = b.querySelector('.btn-text span') || b.querySelector('span');
      if (s) s.textContent = 'Adding...';
      b.style.cursor = 'wait';
      b.style.opacity = '0.8';
    });

    // Stage 2: after 1.2s, freeze + hide sticky (only if main-product click).
    setTimeout(() => {
      const stickyBar = document.querySelector(STICKY_SELECTOR);
      if (stickyBar) {
        const stickyFormId = stickyBar.getAttribute('form');
        // Skip related/recommended cards — their ATC should not hide the sticky.
        const isMainProductClick = buttonsToUpdate.some(b => {
          if (b.closest('.product-card')) return false;
          return b.closest(STICKY_SELECTOR) ||
            (stickyFormId && (b.getAttribute('form') === stickyFormId || b.closest(`form[id="${stickyFormId}"]`)));
        });
        if (isMainProductClick) hideStickyBar();
      }

      buttonsToUpdate.forEach(b => freezeButton(b));

      // Stage 3: guard — re-apply freeze for up to 4s in case the theme
      // re-renders the button (Section Rendering API swap, etc.).
      let checks = 0;
      const interval = setInterval(() => {
        checks++;
        const currentSticky = document.querySelector(STICKY_SELECTOR);
        if (currentSticky && currentSticky.style.display === 'none') hideStickyBar();

        buttonsToUpdate.forEach(b => {
          if (document.body.contains(b) && (!b.closest(STICKY_SELECTOR) || (currentSticky && currentSticky.style.display !== 'none'))) {
            freezeButton(b);
          }
        });

        if (checks > 20) clearInterval(interval);
      }, 200);
    }, 1200);
  }, true);

  // ============================================================
  // 2. Cart cleaner — silently force qty=1 on digital items
  // ============================================================

  function checkAndCleanCart() {
    if (!window.Shopify || !window.Shopify.routes) return;

    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        const updates = {};
        let needsFix = false;

        cart.items.forEach(item => {
          let isDigital = false;
          if (!item.requires_shipping) isDigital = true;
          if (item.product_type === 'Classes' || item.product_type === 'Video Class') isDigital = true;

          if (isDigital && item.quantity > 1) {
            updates[item.id] = 1;
            needsFix = true;
          }
        });

        if (!needsFix) return;

        fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        })
          .then(response => response.json())
          .then(() => {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
            if (window.theme && window.theme.cart) {
              try { window.theme.cart.refresh(); } catch (e) { /* ignore */ }
            }
          })
          .catch(err => console.error('Error cleaning cart:', err));
      })
      .catch(err => console.error('Error fetching cart:', err));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkAndCleanCart);
  } else {
    checkAndCleanCart();
  }
  window.addEventListener('popstate', checkAndCleanCart);
})();
